import React, { useState, useEffect } from 'react';
import { ChevronRight, Lock, Crown, Sparkles, ArrowRight, ChevronLeft } from 'lucide-react';
import API_URL from '../config/API_URL';

// Premium Library Section Component
export const PremiumLibrarySection = ({ 
  isExpanded, 
  onToggle,
  familyData,
  showToast,
  navigate,
  onOpenBookViewer, // Callback to open book viewer
  onOpenTokenPurchase // NEW: Callback to open token purchase modal
}) => {
  const [premiumBooks, setPremiumBooks] = useState([]);
  const [premiumStatus, setPremiumStatus] = useState({
    has_premium: false,
    plan_type: 'free',
    user_role: 'parent',
    story_tokens_remaining: 0,
    share_tokens_remaining: 0,
    ai_generations_used: 0,
    ai_generations_limit: 0
  });
  const [loadingPremium, setLoadingPremium] = useState(true);
  const [premiumPagination, setPremiumPagination] = useState({
    page: 1,
    per_page: 15,
    total_pages: 1,
    total_books: 0,
    has_next: false,
    has_prev: false
  });

  useEffect(() => {
    if (isExpanded) {
      fetchPremiumStatus();
      fetchPremiumBooks();
    }
  }, [isExpanded]);

  const fetchPremiumStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/premium-status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPremiumStatus(data);
      }
    } catch (error) {
      console.error('Error fetching premium status:', error);
    }
  };

  const fetchPremiumBooks = async (page = 1, perPage = 15) => {
    setLoadingPremium(true);
    try {
      const response = await fetch(
        `${API_URL}/api/premium-books?page=${page}&per_page=${perPage}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setPremiumBooks(data.books || []);
        setPremiumPagination(data.pagination || {
          page: 1,
          per_page: perPage,
          total_pages: 1,
          total_books: 0,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error) {
      console.error('Error fetching premium books:', error);
      showToast('Failed to load premium library', 'error');
    } finally {
      setLoadingPremium(false);
    }
  };

  const handlePageChange = (newPage) => {
    fetchPremiumBooks(newPage, premiumPagination.per_page);
  };

  const handlePerPageChange = (newPerPage) => {
    fetchPremiumBooks(1, newPerPage);
  };

  const handleUnlockBook = async (bookId, tokenCost) => {
    try {
      const response = await fetch(`${API_URL}/api/premium-books/${bookId}/unlock`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        showToast(`Book unlocked! ${tokenCost} token${tokenCost > 1 ? 's' : ''} spent`, 'success');
        fetchPremiumBooks(premiumPagination.page, premiumPagination.per_page);
        fetchPremiumStatus();
      } else {
        showToast(data.error || 'Failed to unlock book', 'error');
      }
    } catch (error) {
      console.error('Error unlocking book:', error);
      showToast('Failed to unlock book', 'error');
    }
  };

  // Helper function to get tokens available based on user role
  const getTokensAvailable = () => {
    if (premiumStatus.user_role === 'parent') {
      return premiumStatus.story_tokens_remaining;
    } else {
      return premiumStatus.share_tokens_remaining;
    }
  };

  // Check if user can unlock (has enough tokens or is gold)
  const canUnlock = (tokenCost) => {
    if (premiumStatus.plan_type === 'gold') return true;
    return getTokensAvailable() >= tokenCost;
  };

  return (
    <>
      <style>{`
        @keyframes tokenPulse {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(16, 185, 129, 0.5), 0 4px 6px rgba(0, 0, 0, 0.1);
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 30px rgba(16, 185, 129, 0.7), 0 6px 8px rgba(0, 0, 0, 0.15);
            transform: scale(1.02);
          }
        }
      `}</style>
      
      <div style={{ 
        padding: '1.5rem', 
        marginTop: '2rem',
        marginBottom: '2rem',
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        border: '2px solid',
        borderColor: premiumStatus.has_premium ? '#fbbf24' : '#e5e7eb',
        position: 'relative'
      }}>
        {/* Premium Badge */}
        {premiumStatus.has_premium && (
          <div style={{
            position: 'absolute',
            top: '-12px',
            right: '24px',
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            color: 'white',
            padding: '0.25rem 1rem',
            borderRadius: '1rem',
            fontSize: '0.75rem',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}>
            <Crown size={14} />
            {premiumStatus.plan_type === 'gold' ? 'GOLD' : 'PREMIUM'} MEMBER
          </div>
        )}

        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            marginBottom: isExpanded ? '1.5rem' : '0',
            flexWrap: 'wrap',
            gap: '1rem'
          }}
        >
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem', 
              flexWrap: 'wrap',
              cursor: 'pointer',
              flex: 1
            }}
            onClick={onToggle}
          >
            <div style={{
              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(251, 191, 36, 0.3)'
            }}>
              <Crown size={24} color="white" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold', 
                  color: '#1f2937',
                  margin: 0 
                }}>
                  Premium Library
                </h3>
              </div>
              {premiumStatus.has_premium && (
                <div style={{
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  color: 'white',
                  padding: '0.375rem 1rem',
                  borderRadius: '1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 6px rgba(251, 191, 36, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  whiteSpace: 'nowrap'
                }}>
                  <div style={{
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}>
                    {premiumStatus.user_role === 'child' 
                        ? premiumStatus.share_tokens_remaining
                        : premiumStatus.story_tokens_remaining
                      }
                    </div>
                    <span style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      STORY TOKEN{(premiumStatus.user_role === 'child'
                        ? premiumStatus.share_tokens_remaining
                        : premiumStatus.story_tokens_remaining
                      ) !== 1 ? 'S' : ''}
                  </span>
                </div>
              )}
              {premiumStatus.has_premium && (premiumStatus?.user_role ?? 'parent') !== 'parent' && (
                <div style={{
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  color: 'white',
                  padding: '0.375rem 1rem',
                  borderRadius: '1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 6px rgba(251, 191, 36, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  whiteSpace: 'nowrap'
                }}>
                  <div style={{
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}>
                    {premiumStatus.plan_type === 'gold' 
                      ? premiumStatus.share_tokens_remaining
                      : premiumStatus.user_role !== 'parent'
                      ? premiumStatus.share_tokens_remaining
                      : premiumStatus.share_tokens_remaining
                    }
                  </div>
                  <span style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    SHARE TOKEN{(premiumStatus.plan_type === 'gold' 
                      ? premiumStatus.share_tokens_remaining
                      : premiumStatus.user_role !== 'parent'
                      ? premiumStatus.share_tokens_remaining
                      : premiumStatus.share_tokens_remaining
                    ) !== 1 ? 'S' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {premiumStatus.has_premium && premiumStatus.user_role === 'parent' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenTokenPurchase) {
                    onOpenTokenPurchase();
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '1.5rem',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.3)';
                }}
              >
                <Sparkles size={16} />
                <span>BUY TOKENS</span>
              </button>
            )}

            {premiumStatus.has_premium && premiumStatus.user_role !== 'parent' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenTokenPurchase) {
                    onOpenTokenPurchase();
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '1.5rem',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.3)';
                }}
              >
                <Sparkles size={16} />
                <span>BUY TOKENS</span>
              </button>
            )}
            
            
            <div 
              onClick={onToggle}
              style={{ 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ChevronRight 
                size={24} 
                style={{ 
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', 
                  transition: 'transform 0.2s',
                  color: '#6b7280'
                }} 
              />
            </div>
          </div>
        </div>

        {isExpanded && (
          <>
            {!premiumStatus.has_premium ? (
              // Lock screen for non-premium users
              <div style={{
                textAlign: 'center',
                padding: '4rem 2rem',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '0.75rem',
                marginTop: '1rem'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '50%',
                  width: '80px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  <Lock size={40} color="#f59e0b" />
                </div>
                <h4 style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#92400e',
                  marginBottom: '1rem'
                }}>
                  Unlock Premium Stories
                </h4>
                <p style={{
                  color: '#92400e',
                  marginBottom: '1.5rem',
                  maxWidth: '400px',
                  margin: '0 auto 1.5rem'
                }}>
                  Get access to our exclusive collection of premium stories with beautiful illustrations and engaging narratives.
                </p>
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  marginBottom: '2rem'
                }}>
                  <div style={{
                    background: 'white',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    <Sparkles size={24} color="#f59e0b" style={{ marginBottom: '0.5rem' }} />
                    <p style={{ fontWeight: '600', color: '#92400e' }}>Premium Plan</p>
                    <p style={{ fontSize: '0.875rem', color: '#92400e' }}>5 tokens/month</p>
                  </div>
                  <div style={{
                    background: 'white',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    <Crown size={24} color="#f59e0b" style={{ marginBottom: '0.5rem' }} />
                    <p style={{ fontWeight: '600', color: '#92400e' }}>Gold Plan</p>
                    <p style={{ fontSize: '0.875rem', color: '#92400e' }}>15 tokens/month + more</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/subscription')}
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: 'white',
                    padding: '0.75rem 2rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 4px 6px rgba(245, 158, 11, 0.3)',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <Crown size={20} />
                  Subscribe Now
                </button>
              </div>
            ) : (
              // Premium content for subscribed users
              <>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '1.5rem', 
                  flexWrap: 'wrap', 
                  gap: '1rem' 
                }}>
                  <p style={{ color: '#4b5563', margin: 0 }}>
                    Exclusive premium stories for your family
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Show:</span>
                    <select
                      value={premiumPagination.per_page}
                      onChange={(e) => handlePerPageChange(Number(e.target.value))}
                      style={{
                        padding: '0.375rem 0.75rem',
                        borderRadius: '0.375rem',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem',
                        cursor: 'pointer'
                      }}
                    >
                      <option value={15}>15</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>per page</span>
                  </div>
                </div>

                {loadingPremium ? (
                  <p style={{ color: '#6b7280', textAlign: 'center' }}>Loading premium books...</p>
                ) : premiumBooks.length === 0 ? (
                  <p style={{ color: '#6b7280', textAlign: 'center' }}>No premium books available yet.</p>
                ) : (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                    gap: '1.5rem' 
                  }}>
                    {premiumBooks.map(book => (
                      <div
                        key={book.id}
                        style={{
                          border: book.is_unlocked ? '2px solid #fbbf24' : '1px solid #e5e7eb',
                          borderRadius: '0.75rem',
                          padding: '1.25rem',
                          textAlign: 'center',
                          background: book.is_unlocked 
                            ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' 
                            : 'white',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          gap: '1rem',
                          position: 'relative'
                        }}
                      >
                        {!book.is_unlocked && (
                          <div style={{
                            position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            background: 'rgba(245, 158, 11, 0.1)',
                            borderRadius: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            <Lock size={12} color="#f59e0b" />
                            <span style={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: '600' }}>
                              {book.token_cost} {book.token_cost === 1 ? 'token' : 'tokens'}
                            </span>
                          </div>
                        )}
                        
                        {book.is_unlocked && (
                          <div style={{
                            position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                            borderRadius: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            color: 'white',
                            fontSize: '0.625rem',
                            fontWeight: '700'
                          }}>
                            UNLOCKED
                          </div>
                        )}
                        
                        <div>
                          {book.pdf_url && book.is_unlocked ? (
                            <div style={{ 
                              width: '100%', 
                              height: '250px', 
                              marginBottom: '0.75rem',
                              border: '1px solid #fbbf24',
                              borderRadius: '0.5rem',
                              overflow: 'hidden',
                              backgroundColor: '#fffbeb'
                            }}>
                              <iframe 
                                src={`${book.pdf_url.trim()}#page=1&view=FitH`}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  border: 'none',
                                  pointerEvents: 'none'
                                }}
                                title={`${book.title} preview`}
                              />
                            </div>
                          ) : (
                            <div style={{ 
                              width: '100%', 
                              height: '250px', 
                              marginBottom: '0.75rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.5rem',
                              backgroundColor: '#f9fafb',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative'
                            }}>
                              {!book.is_unlocked && (
                                <div style={{
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'rgba(255, 255, 255, 0.9)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '0.5rem'
                                }}>
                                  <Lock size={32} color="#9ca3af" />
                                  <p style={{ 
                                    marginTop: '0.5rem', 
                                    fontSize: '0.875rem', 
                                    color: '#6b7280' 
                                  }}>
                                    Unlock to preview
                                  </p>
                                </div>
                              )}
                              <span style={{ fontSize: '3.5rem' }}>📚</span>
                            </div>
                          )}
                          <h4 style={{ fontWeight: '600', color: '#1f2937', fontSize: '1.125rem' }}>
                            {book.title}
                          </h4>
                          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            by {book.author}
                          </p>
                          {book.description && (
                            <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                              {book.description}
                            </p>
                          )}
                        </div>
                        
                        {book.is_unlocked ? (
                          <button
                            onClick={() => {
                              // Use the callback to open book viewer in parent component
                              if (onOpenBookViewer) {
                                onOpenBookViewer(book);
                              }
                            }}
                            style={{
                              width: '100%',
                              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                              color: 'white',
                              padding: '0.625rem',
                              borderRadius: '0.375rem',
                              border: 'none',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem',
                              transition: 'transform 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            View Story <ArrowRight size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              if (canUnlock(book.token_cost)) {
                                handleUnlockBook(book.id, book.token_cost);
                              } else {
                                showToast(`Not enough tokens. Need ${book.token_cost}, have ${getTokensAvailable()}`, 'error');
                              }
                            }}
                            disabled={!canUnlock(book.token_cost)}
                            style={{
                              width: '100%',
                              background: canUnlock(book.token_cost)
                                ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                                : '#d1d5db',
                              color: 'white',
                              padding: '0.625rem',
                              borderRadius: '0.375rem',
                              border: 'none',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              cursor: canUnlock(book.token_cost) ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem',
                              transition: 'transform 0.2s',
                              opacity: canUnlock(book.token_cost) ? 1 : 0.5
                            }}
                            onMouseEnter={(e) => {
                              if (canUnlock(book.token_cost)) {
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (canUnlock(book.token_cost)) {
                                e.currentTarget.style.transform = 'scale(1)';
                              }
                            }}
                          >
                            <Lock size={16} />
                            Unlock ({book.token_cost} {book.token_cost === 1 ? 'token' : 'tokens'})
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  paddingTop: '1.5rem',
                  borderTop: '1px solid #e5e7eb',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginTop: '1.5rem'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Showing {((premiumPagination.page - 1) * premiumPagination.per_page) + 1} to{' '}
                    {Math.min(premiumPagination.page * premiumPagination.per_page, premiumPagination.total_books)} of{' '}
                    {premiumPagination.total_books} premium books
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      onClick={() => handlePageChange(premiumPagination.page - 1)}
                      disabled={!premiumPagination.has_prev}
                      style={{
                        padding: '0.5rem 0.75rem',
                        borderRadius: '0.375rem',
                        border: '1px solid #d1d5db',
                        background: premiumPagination.has_prev ? 'white' : '#f3f4f6',
                        color: premiumPagination.has_prev ? '#374151' : '#9ca3af',
                        cursor: premiumPagination.has_prev ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <ChevronLeft size={16} /> Previous
                    </button>

                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      {Array.from({ length: premiumPagination.total_pages }, (_, i) => i + 1)
                        .filter(pageNum => {
                          return pageNum === 1 || 
                                 pageNum === premiumPagination.total_pages ||
                                 Math.abs(pageNum - premiumPagination.page) <= 1;
                        })
                        .map((pageNum, index, array) => {
                          const showEllipsis = index > 0 && pageNum - array[index - 1] > 1;
                          
                          return (
                            <React.Fragment key={pageNum}>
                              {showEllipsis && (
                                <span style={{ padding: '0.5rem', color: '#9ca3af' }}>...</span>
                              )}
                              <button
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  borderRadius: '0.375rem',
                                  border: '1px solid #d1d5db',
                                  background: pageNum === premiumPagination.page 
                                    ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' 
                                    : 'white',
                                  color: pageNum === premiumPagination.page ? 'white' : '#374151',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: pageNum === premiumPagination.page ? 'bold' : 'normal',
                                  minWidth: '2.5rem'
                                }}
                              >
                                {pageNum}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>

                    <button
                      onClick={() => handlePageChange(premiumPagination.page + 1)}
                      disabled={!premiumPagination.has_next}
                      style={{
                        padding: '0.5rem 0.75rem',
                        borderRadius: '0.375rem',
                        border: '1px solid #d1d5db',
                        background: premiumPagination.has_next ? 'white' : '#f3f4f6',
                        color: premiumPagination.has_next ? '#374151' : '#9ca3af',
                        cursor: premiumPagination.has_next ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      Next <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};